import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, Clock, ExternalLink, FileText, Linkedin, Globe, ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';

interface Application {
  id: string;
  user_id: string;
  organization: string;
  role: string;
  experience: string;
  portfolio_url: string | null;
  linkedin_url: string | null;
  press_card_url: string | null;
  additional_info: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
  profiles: {
    username: string;
    photo_url: string;
    email?: string;
  };
}

const AdminDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser?.is_admin) {
      navigate('/home');
      return;
    }

    fetchApplications();
  }, [currentUser, filter, navigate]);

  const fetchApplications = async () => {
    try {
      setIsLoading(true);
      let query = supabase
        .from('verification_applications')
        .select(`
          *,
          profiles:user_id (
            username,
            photo_url
          )
        `)
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setApplications(data || []);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load applications');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (applicationId: string, newStatus: 'approved' | 'rejected') => {
    try {
      setProcessingId(applicationId);
      setError('');

      const { error: updateError } = await supabase
        .from('verification_applications')
        .update({ status: newStatus })
        .eq('id', applicationId);

      if (updateError) throw updateError;

      await fetchApplications();
    } catch (err) {
      console.error('Error updating application:', err);
      setError('Failed to update application status');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: { icon: Clock, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50', label: 'Pending' },
      approved: { icon: CheckCircle, color: 'bg-green-500/20 text-green-400 border-green-500/50', label: 'Approved' },
      rejected: { icon: XCircle, color: 'bg-red-500/20 text-red-400 border-red-500/50', label: 'Rejected' }
    };

    const badge = badges[status as keyof typeof badges];
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${badge.color}`}>
        <Icon className="h-4 w-4" />
        {badge.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!currentUser?.is_admin) {
    return (
      <div className="max-w-2xl mx-auto mt-20">
        <div className="bg-red-900/50 border border-red-700 text-red-200 px-6 py-4 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-1">Access Denied</p>
            <p className="text-sm">You don't have permission to access the admin dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-purple-400" />
            <h1 className="text-2xl font-bold text-white">Verification Applications</h1>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="mb-6 flex gap-2">
        {(['all', 'pending', 'approved', 'rejected'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors capitalize ${
              filter === status
                ? 'bg-purple-500 text-white'
                : 'bg-[#272727] text-gray-400 hover:text-white hover:bg-[#404040]'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {applications.length === 0 ? (
        <div className="text-center py-12 bg-[#272727] rounded-xl">
          <Shield className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-300 mb-2">No Applications Found</h3>
          <p className="text-gray-400">
            {filter === 'pending' ? 'There are no pending applications to review.' : `No ${filter} applications.`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <div key={app.id} className="bg-[#272727] rounded-xl p-6 hover:bg-[#323232] transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <img
                    src={app.profiles.photo_url}
                    alt={app.profiles.username}
                    className="w-16 h-16 rounded-full object-cover border-2 border-purple-500"
                  />
                  <div>
                    <h3 className="text-xl font-semibold text-white">{app.profiles.username}</h3>
                    <p className="text-gray-400 text-sm">Applied {format(new Date(app.created_at), 'MMM d, yyyy')}</p>
                  </div>
                </div>
                {getStatusBadge(app.status)}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Organization</p>
                  <p className="text-white font-medium">{app.organization}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Role</p>
                  <p className="text-white font-medium">{app.role}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Experience</p>
                  <p className="text-white font-medium">{app.experience} years</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Status Updated</p>
                  <p className="text-white font-medium">{format(new Date(app.updated_at), 'MMM d, yyyy')}</p>
                </div>
              </div>

              {app.additional_info && (
                <div className="mb-4">
                  <p className="text-sm text-gray-400 mb-1">Additional Information</p>
                  <p className="text-gray-300 text-sm">{app.additional_info}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-3 mb-4">
                {app.portfolio_url && (
                  <a
                    href={app.portfolio_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#404040] text-gray-300 rounded-lg hover:bg-[#505050] transition-colors text-sm"
                  >
                    <Globe className="h-4 w-4" />
                    Portfolio
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {app.linkedin_url && (
                  <a
                    href={app.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#404040] text-gray-300 rounded-lg hover:bg-[#505050] transition-colors text-sm"
                  >
                    <Linkedin className="h-4 w-4" />
                    LinkedIn
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {app.press_card_url && (
                  <a
                    href={app.press_card_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#404040] text-gray-300 rounded-lg hover:bg-[#505050] transition-colors text-sm"
                  >
                    <FileText className="h-4 w-4" />
                    Press Card
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>

              {app.status === 'pending' && (
                <div className="flex gap-3 pt-4 border-t border-[#404040]">
                  <button
                    onClick={() => handleUpdateStatus(app.id, 'approved')}
                    disabled={processingId === app.id}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-green-600/50 disabled:cursor-not-allowed font-medium"
                  >
                    {processingId === app.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5" />
                        Approve
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(app.id, 'rejected')}
                    disabled={processingId === app.id}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-600/50 disabled:cursor-not-allowed font-medium"
                  >
                    {processingId === app.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <XCircle className="h-5 w-5" />
                        Reject
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
